import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Search, Pencil, Trash2, Power } from "lucide-react";
import { Input } from "@/components/ui/input";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { PageHeader } from "@/components/PageHeader";
import { UserFormDialog } from "@/components/UserFormDialog";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg } from "@/lib/api";

const RoleBadge = ({ u }) => {
  const c = { admin: "bg-[#1E4D2B] text-white", tecnico: "bg-[#D97706]/15 text-[#9A5300]", cliente: "bg-[#E8F5E9] text-[#1E4D2B]" }[u.role];
  return <span className={`text-xs font-bold px-2.5 py-1 rounded-full ${c}`}>{u.role_label}</span>;
};

const IconBtn = ({ children, ...p }) => (
  <button className="h-12 w-12 rounded-xl border border-[#E2DDD3] flex items-center justify-center hover:bg-[#E8F5E9] hover:border-[#1E4D2B] transition-colors" {...p}>{children}</button>
);

const UserRow = ({ u, me, onEdit, onToggle, onDelete }) => (
  <div className={`bg-white rounded-2xl border border-[#E2DDD3] p-4 flex flex-col sm:flex-row sm:items-center gap-4 ${u.active ? "" : "opacity-60"}`} data-testid={`user-row-${u.user_id}`}>
    <div className="flex items-center gap-3 flex-1 min-w-0">
      <div className="h-12 w-12 rounded-full bg-[#C86D51] text-white font-bold flex items-center justify-center shrink-0">{u.name[0]}</div>
      <div className="min-w-0">
        <p className="font-semibold truncate">{u.name} {!u.active && <span className="text-xs text-[#B05A40]">(inativo)</span>}</p>
        <p className="text-sm text-[#5C6B66] truncate">{u.email}{u.phone ? ` · ${u.phone}` : ""}</p>
      </div>
    </div>
    <div className="flex items-center gap-2 justify-between sm:justify-end">
      <RoleBadge u={u} />
      {u.user_id !== me && (
        <div className="flex gap-2">
          <IconBtn onClick={() => onEdit(u)} aria-label="Editar" data-testid={`user-edit-${u.user_id}`}><Pencil className="h-5 w-5" /></IconBtn>
          <IconBtn onClick={() => onToggle(u)} aria-label="Ativar/Desativar" data-testid={`user-toggle-${u.user_id}`}><Power className="h-5 w-5" /></IconBtn>
          <IconBtn onClick={() => onDelete(u)} aria-label="Excluir" data-testid={`user-delete-${u.user_id}`}><Trash2 className="h-5 w-5 text-[#B05A40]" /></IconBtn>
        </div>
      )}
    </div>
  </div>
);

export default function UsersPage({ title, subtitle, roles, testId, embedded = false }) {
  const { user } = useAuth();
  const [list, setList] = useState([]);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [toDelete, setToDelete] = useState(null);

  const load = useCallback(() => {
    api.get("/users", { params: { role: roles.join(","), q: q || undefined } }).then(({ data }) => setList(data)).catch((e) => toast.error(errMsg(e)));
  }, [roles, q]);
  useEffect(() => { load(); }, [load]);

  const toggle = async (u) => {
    try { await api.put(`/users/${u.user_id}`, { active: !u.active }); toast.success(u.active ? "Usuário desativado" : "Usuário ativado"); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const remove = async () => {
    try { await api.delete(`/users/${toDelete.user_id}`); toast.success("Usuário excluído"); setToDelete(null); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };

  const addBtn = (
    <button onClick={() => { setEditing(null); setOpen(true); }} data-testid="user-add-button"
      className="h-14 px-6 rounded-xl bg-[#1E4D2B] text-white font-semibold flex items-center justify-center gap-2 hover:bg-[#163A20] active:scale-[0.98] transition-[background-color,transform]">
      <Plus className="h-5 w-5" /> Novo
    </button>
  );

  return (
    <div data-testid={testId}>
      {embedded ? <div className="flex justify-end mb-4">{addBtn}</div> : <PageHeader eyebrow="Cadastros" title={title} subtitle={subtitle} action={addBtn} />}      <div className="relative mb-5">
        <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-[#5C6B66]" />
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar por nome ou e-mail" className="h-14 pl-12 rounded-xl bg-white text-base" data-testid="user-search-input" />
      </div>
      <div className="space-y-3" data-testid="user-list">
        {list.length === 0 && <p className="text-center py-10 text-[#5C6B66]" data-testid="user-list-empty">Nenhum usuário encontrado.</p>}
        {list.map((u) => <UserRow key={u.user_id} u={u} me={user.user_id} onEdit={(x) => { setEditing(x); setOpen(true); }} onToggle={toggle} onDelete={setToDelete} />)}
      </div>
      <UserFormDialog open={open} onOpenChange={setOpen} editing={editing} roles={roles} onSaved={load} />
      <AlertDialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)}>
        <AlertDialogContent className="rounded-3xl">
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir usuário?</AlertDialogTitle>
            <AlertDialogDescription>{toDelete?.name} perderá o acesso ao sistema. Esta ação não pode ser desfeita.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="h-12" data-testid="user-delete-cancel">Cancelar</AlertDialogCancel>
            <AlertDialogAction className="h-12 bg-[#B05A40] hover:bg-[#8f4733]" onClick={remove} data-testid="user-delete-confirm">Excluir</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
